import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { apiRequest } from '../api/client';
import { getRuntimeConfig } from '../app/config/runtime-config';
import {
  changeCognitoPassword,
  confirmNewPassword,
  getCognitoProfileAttributes,
  hasCognitoSession,
  signInWithPassword,
  signOutCognito,
} from './auth-client';
import { setApiCustomerId } from './customer-context';
import { clearFirstMachineActivationSession } from './first-machine-activation-session';
import { activateRoute42Memberships } from './membership-client';
import type { Route42Membership, Route42UserProfile, SignInChallenge } from './types';
import { mockProfile } from '../api/mock-transport';

const ORGANIZATION_SESSION_KEY = 'route42:active-organization';
const LEGACY_CUSTOMER_SESSION_KEY = 'route42:active-customer';
const LAST_ORGANIZATION_KEY_PREFIX = 'route42:last-organization:';
const MOCK_AUTH_SESSION_KEY = 'route42:mock-authenticated';

interface AuthContextValue {
  profile: Route42UserProfile | null;
  loading: boolean;
  challenge: SignInChallenge;
  challengeRequiredAttributes: readonly string[];
  emailVerificationRequired: boolean;
  activeCustomerId: string | null;
  activeMembership: Route42Membership | null;
  permissions: ReadonlySet<string>;
  signIn: (username: string, password: string) => Promise<SignInChallenge>;
  confirmPassword: (password: string, userAttributes?: Record<string, string>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  selectCustomer: (customerId: string) => void;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function availableMemberships(profile: Route42UserProfile | null): Route42Membership[] {
  return profile?.memberships.filter((membership) => membership.access_available) ?? [];
}

function lastOrganizationKey(profile: Route42UserProfile | null): string | null {
  const userId = profile?.user.user_id?.trim();
  return userId ? `${LAST_ORGANIZATION_KEY_PREFIX}${userId}` : null;
}

function storageGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Organization memory is a convenience. Authentication and tenant
    // authorization must continue to work when browser storage is unavailable.
  }
}

function storageRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Best-effort cleanup only.
  }
}

function apiErrorCode(reason: unknown): string | null {
  if (!reason || typeof reason !== 'object' || !('code' in reason)) return null;
  const value = (reason as { code?: unknown }).code;
  return typeof value === 'string' ? value : null;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const config = getRuntimeConfig();
  const [profile, setProfile] = useState<Route42UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState<SignInChallenge>(null);
  const [challengeRequiredAttributes, setChallengeRequiredAttributes] = useState<string[]>([]);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);

  const applyCustomerChoice = useCallback((nextProfile: Route42UserProfile | null) => {
    const memberships = availableMemberships(nextProfile);
    const sessionChoice = storageGet(sessionStorage, ORGANIZATION_SESSION_KEY) ?? storageGet(sessionStorage, LEGACY_CUSTOMER_SESSION_KEY);
    const sessionChoiceValid = memberships.some((membership) => membership.customer_id === sessionChoice);
    const preferenceKey = lastOrganizationKey(nextProfile);
    const rememberedChoice = preferenceKey ? storageGet(localStorage, preferenceKey) : null;
    const rememberedChoiceValid = memberships.some((membership) => membership.customer_id === rememberedChoice);
    const nextCustomer =
      memberships.length === 1
        ? memberships[0].customer_id
        : sessionChoiceValid
          ? sessionChoice
          : rememberedChoiceValid
            ? rememberedChoice
            : null;

    storageRemove(sessionStorage, LEGACY_CUSTOMER_SESSION_KEY);
    if (nextCustomer) {
      storageSet(sessionStorage, ORGANIZATION_SESSION_KEY, nextCustomer);
      if (preferenceKey) storageSet(localStorage, preferenceKey, nextCustomer);
    } else {
      storageRemove(sessionStorage, ORGANIZATION_SESSION_KEY);
      if (preferenceKey && rememberedChoice && !rememberedChoiceValid) storageRemove(localStorage, preferenceKey);
    }

    setActiveCustomerId(nextCustomer);
    setApiCustomerId(nextCustomer);
  }, []);

  const refreshProfile = useCallback(async () => {
    let nextProfile =
      config.authMode === 'mock'
        ? structuredClone(mockProfile)
        : await apiRequest<Route42UserProfile>('/v1/auth/me', { customerScoped: false });

    if (config.authMode === 'cognito') {
      try {
        const attributes = await getCognitoProfileAttributes();
        nextProfile = {
          ...nextProfile,
          user: {
            ...nextProfile.user,
            email: attributes.email ?? nextProfile.user.email,
            email_verified: attributes.email ? attributes.emailVerified : nextProfile.user.email_verified,
          },
        };
      } catch {
        // /v1/auth/me remains authoritative for Route42 membership context. A
        // Cognito attribute read failure should not prevent the workspace from
        // loading; the Profile screen simply falls back to the Route42 identity.
      }
    }

    setProfile(nextProfile);
    setEmailVerificationRequired(false);
    applyCustomerChoice(nextProfile);
  }, [applyCustomerChoice, config.authMode]);

  useEffect(() => {
    let mounted = true;
    async function initialize() {
      try {
        if (config.authMode === 'mock') {
          if (mounted && sessionStorage.getItem(MOCK_AUTH_SESSION_KEY) === '1') {
            setProfile(structuredClone(mockProfile));
            applyCustomerChoice(mockProfile);
          }
          return;
        }
        if (await hasCognitoSession()) {
          try {
            await activateRoute42Memberships();
          } catch (reason) {
            if (apiErrorCode(reason) === 'EMAIL_NOT_VERIFIED') {
              if (mounted) setEmailVerificationRequired(true);
              return;
            }
            throw reason;
          }
          await refreshProfile();
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    void initialize();
    return () => {
      mounted = false;
    };
  }, [applyCustomerChoice, config.authMode, refreshProfile]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      setLoading(true);
      try {
        if (config.authMode === 'mock') {
          sessionStorage.setItem(MOCK_AUTH_SESSION_KEY, '1');
          setChallenge(null);
          setChallengeRequiredAttributes([]);
          await refreshProfile();
          return null;
        }
        const result = await signInWithPassword(username, password);
        if (result.status === 'NEW_PASSWORD_REQUIRED') {
          setChallenge('NEW_PASSWORD_REQUIRED');
          setChallengeRequiredAttributes(result.missingAttributes);
          return 'NEW_PASSWORD_REQUIRED';
        }
        setChallenge(null);
        setChallengeRequiredAttributes([]);
        try {
          await activateRoute42Memberships();
        } catch (reason) {
          if (apiErrorCode(reason) === 'EMAIL_NOT_VERIFIED') setEmailVerificationRequired(true);
          throw reason;
        }
        await refreshProfile();
        return null;
      } finally {
        setLoading(false);
      }
    },
    [config.authMode, refreshProfile],
  );

  const confirmPassword = useCallback(
    async (password: string, userAttributes: Record<string, string> = {}) => {
      setLoading(true);
      try {
        if (config.authMode !== 'mock') {
          await confirmNewPassword(password, userAttributes);
          try {
            await activateRoute42Memberships();
          } catch (reason) {
            if (apiErrorCode(reason) === 'EMAIL_NOT_VERIFIED') setEmailVerificationRequired(true);
            throw reason;
          }
        }
        setChallenge(null);
        setChallengeRequiredAttributes([]);
        await refreshProfile();
      } finally {
        setLoading(false);
      }
    },
    [config.authMode, refreshProfile],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (config.authMode === 'mock') {
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        if (currentPassword !== 'route42-local') throw new Error('Current password is incorrect.');
        return;
      }
      await changeCognitoPassword(currentPassword, newPassword);
    },
    [config.authMode],
  );

  const signOut = useCallback(async () => {
    if (config.authMode !== 'mock') await signOutCognito();
    clearFirstMachineActivationSession(activeCustomerId);
    storageRemove(sessionStorage, MOCK_AUTH_SESSION_KEY);
    storageRemove(sessionStorage, ORGANIZATION_SESSION_KEY);
    storageRemove(sessionStorage, LEGACY_CUSTOMER_SESSION_KEY);
    setApiCustomerId(null);
    setActiveCustomerId(null);
    setProfile(null);
    setChallenge(null);
    setChallengeRequiredAttributes([]);
    setEmailVerificationRequired(false);
  }, [activeCustomerId, config.authMode]);

  const selectCustomer = useCallback(
    (customerId: string) => {
      const valid = availableMemberships(profile).some(
        (membership) => membership.customer_id === customerId,
      );
      if (!valid) throw new Error('The selected customer is not available to this user.');
      storageSet(sessionStorage, ORGANIZATION_SESSION_KEY, customerId);
      const preferenceKey = lastOrganizationKey(profile);
      if (preferenceKey) storageSet(localStorage, preferenceKey, customerId);
      setActiveCustomerId(customerId);
      setApiCustomerId(customerId);
    },
    [profile],
  );

  const activeMembership = useMemo(
    () =>
      profile?.memberships.find(
        (membership) => membership.customer_id === activeCustomerId && membership.access_available,
      ) ?? null,
    [activeCustomerId, profile],
  );
  const permissions = useMemo(
    () => new Set(activeMembership?.effective_permissions ?? []),
    [activeMembership],
  );
  const can = useCallback((permission: string) => permissions.has(permission), [permissions]);

  const value = useMemo<AuthContextValue>(
    () => ({
      profile,
      loading,
      challenge,
      challengeRequiredAttributes,
      emailVerificationRequired,
      activeCustomerId,
      activeMembership,
      permissions,
      signIn,
      confirmPassword,
      changePassword,
      signOut,
      refreshProfile,
      selectCustomer,
      can,
    }),
    [
      activeCustomerId,
      activeMembership,
      can,
      challenge,
      challengeRequiredAttributes,
      emailVerificationRequired,
      changePassword,
      confirmPassword,
      loading,
      permissions,
      profile,
      refreshProfile,
      selectCustomer,
      signIn,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider.');
  return value;
}
